"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Img_1 from "../../assets/steps_cards/img_1.webp";
import Img_2 from "../../assets/steps_cards/img_2.webp";
import Img_3 from "../../assets/steps_cards/img_3.webp";
import CommonLayout from "./CommonLayout";

const accordionItems = [
  {
    step: 1,
    title: "Step 1 - Decide your trip",
    image: Img_1,
  },
  {
    step: 2,
    title: "Step 2 - Pick your ride",
    image: Img_2,
  },
  {
    step: 3,
    title: "Step 3 - Enjoy Your Ride",
    image: Img_3,
  },
];
const ScrollAccordion: React.FC<{ cityName?: string }> = ({ cityName }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isScrolling, setIsScrolling] = useState(false);
  const heroSectionRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);

  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      if (!isScrolling) {
        setIsScrolling(true);
      }

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        setIsScrolling(false);
      }, 100);

      const currentScrollY = window.scrollY;
      const heroSection = heroSectionRef.current;

      if (heroSection) {
        const sectionTop = heroSection.offsetTop;
        const sectionHeight = heroSection.offsetHeight;
        const windowHeight = window.innerHeight;

        // Calculate if we're in the hero section
        if (
          currentScrollY + windowHeight / 2 >= sectionTop &&
          currentScrollY <= sectionTop + sectionHeight
        ) {
          // Calculate progress through the section (0 to 1)
          const progress = Math.max(
            0,
            Math.min(
              1,
              (currentScrollY - sectionTop) / (sectionHeight - windowHeight)
            )
          );

          // Determine which step should be active based on progress
          let targetStep = Math.floor(progress * 3) + 1;
          targetStep = Math.max(1, Math.min(3, targetStep));

          setCurrentStep(targetStep);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isScrolling]);

  return (
    <CommonLayout
      className="flex items-start justify-between min-h-screen gap-8 md:gap-16 md:flex-row flex-col text-left"
      ref={heroSectionRef}
    >
      {/* Left Content */}
      <div className="flex flex-col items-center justify-center mt-8 md:mt-0 static h-auto md:sticky md:top-8 md:h-[calc(100vh-4rem)]">
        <div className="w-full">
        <h2 className="text-[32px] font-medium text-black mb-4">
          {cityName ? `Book your ride in ${cityName} in` : 'Book your ride in'}
          <br className="hidden md:block"/>
          <strong className="!text-[#F05A24]"> 3 easy steps.</strong>
          <br className="hidden md:block"/>
          That simple.
        </h2>
        </div>

        <div className="hidden md:flex items-center w-full">
          <button 
            className="h-11 flex items-center bg-gray-100 text-gray-500 font-normal border border-gray-300 text-[12px] px-8 rounded-full transition cursor-not-allowed"
            disabled
            aria-label="Fareculator feature coming soon"
          >
            Try Fareculator
            <br /> COMING SOON
          </button>
          {/* <SearchRedirectBtn
            icon={<SearchIcon className="w-5 h-5" />}
            className="h-11 w-30 ml-4"
            analyticsName="scroll_animation_search_button"
          >
            Search
          </SearchRedirectBtn> */}
        </div>
      </div>

      {/* Right Accordion */}
      <div className="w-full flex-1 relative min-h-[200vh] md:min-h-[400vh]">
        <div className="sticky top-8 max-w-2xl ml-auto h-[70vh] md:h-[calc(100vh-4rem)] flex flex-col justify-center gap-8 md:gap-4 pb-8 md:pb-0">
          {accordionItems.map((item) => (
            <div
              key={item.step}
              className="transition-all duration-700 ease-out"
            >
              {/* Accordion Header - Only show when not active */}
              {currentStep !== item.step && (
                <div
                  className={`py-6 flex items-center justify-between rounded-2xl border border-gray-300`}
                >
                  <h3 className="text-xl font-semibold text-center w-full">
                    {item.title}
                  </h3>
                </div>
              )}
              {/* Accordion Content - Only show image when active */}
              <div
                className={`overflow-hidden transition-all duration-700 ease-out ${
                  currentStep === item.step ? "h-90 md:max-h-96" : "max-h-0 p-0"
                }`}
              >
                <Image
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-fill"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </CommonLayout>
  );
};

export default ScrollAccordion;
